import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/** Merge refs for asChild + Radix Slot. */
function composeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (value) => {
    for (const ref of refs) {
      if (ref == null) continue
      if (typeof ref === "function") {
        ref(value)
      } else {
        ;(ref as React.MutableRefObject<T | null>).current = value
      }
    }
  }
}

const legacyButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        large: "h-10 rounded-md px-6 has-[>svg]:px-4",
        small: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Figma-aligned button styles (hierarchy × mode × size).
 * Library node 230:2775 — corner/corner-40, Label/L + Body/M bold, primitives from style.css.
 */
const figmaButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--corner-40)] transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      hierarchy: {
        primary: "",
        secondary: "",
        tertiary: "",
      },
      mode: {
        light: "",
        dark: "",
      },
      size: {
        large:
          "min-h-[52px] px-6 py-4 label-l has-[>svg]:px-5 [&_svg:not([class*='size-'])]:size-6",
        small:
          "min-h-11 px-4 py-3 body-copy-m-bold [&_svg:not([class*='size-'])]:size-5",
        "icon-large":
          "size-[52px] p-0 [&_svg:not([class*='size-'])]:size-6 rounded-[var(--corner-40)]",
        "icon-small":
          "size-11 p-0 [&_svg:not([class*='size-'])]:size-5 rounded-[var(--corner-40)]",
      },
    },
    compoundVariants: [
      /* Primary — Figma: Brown/20 fill (light), Sand/95 or Neutral/White on dark; hover Brown/30 (light) / Brown/90 (dark); inactive status neutrals */
      {
        hierarchy: "primary",
        mode: "light",
        class:
          "bg-primary text-primary-foreground hover:bg-[var(--brown-30)] focus-visible:ring-primary disabled:bg-[var(--status-neutral-90)] disabled:text-[var(--status-neutral-40)] disabled:hover:bg-[var(--status-neutral-90)] disabled:opacity-100",
      },
      {
        hierarchy: "primary",
        mode: "dark",
        size: "large",
        class:
          "bg-neutral-white text-[var(--neutral-10)] hover:bg-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/60 disabled:bg-[var(--status-neutral-50)] disabled:text-[var(--status-neutral-95)] disabled:hover:bg-[var(--status-neutral-50)] disabled:opacity-100",
      },
      {
        hierarchy: "primary",
        mode: "dark",
        size: "icon-large",
        class:
          "bg-neutral-white text-[var(--neutral-10)]  hover:bg-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/60 disabled:bg-[var(--status-neutral-50)] disabled:text-[var(--status-neutral-95)] disabled:hover:bg-[var(--status-neutral-50)] disabled:opacity-100",
      },
      {
        hierarchy: "primary",
        mode: "dark",
        size: "small",
        class:
          "bg-[var(--sand-95)] text-[var(--neutral-10)]  hover:bg-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/60 disabled:bg-[var(--status-neutral-90)] disabled:text-[var(--status-neutral-40)] disabled:hover:bg-[var(--status-neutral-90)] disabled:opacity-100",
      },
      {
        hierarchy: "primary",
        mode: "dark",
        size: "icon-small",
        class:
          "bg-[var(--sand-95)] text-[var(--neutral-10)]  hover:bg-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/60 disabled:bg-[var(--status-neutral-90)] disabled:text-[var(--status-neutral-40)] disabled:hover:bg-[var(--status-neutral-90)] disabled:opacity-100",
      },
      /* Secondary — outline Neutral/10 (light) or Neutral/White (dark); hover Brown/30 + text (light) / Brown/90 (dark); inactive border + text status neutral 60 */
      {
        hierarchy: "secondary",
        mode: "light",
        class:
          "border border-[var(--neutral-10)] bg-transparent text-[var(--neutral-10)] hover:border-[var(--brown-30)] hover:bg-transparent hover:text-[var(--brown-30)] focus-visible:ring-primary disabled:border-[var(--status-neutral-60)] disabled:text-[var(--status-neutral-60)] disabled:hover:border-[var(--status-neutral-60)] disabled:hover:text-[var(--status-neutral-60)] disabled:opacity-100",
      },
      {
        hierarchy: "secondary",
        mode: "dark",
        class:
          "border border-[var(--neutral-white)] bg-transparent text-[var(--neutral-white)] hover:border-[var(--brown-90)] hover:bg-transparent hover:text-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/40 disabled:border-[var(--status-neutral-60)] disabled:text-[var(--status-neutral-60)] disabled:hover:border-[var(--status-neutral-60)] disabled:hover:text-[var(--status-neutral-60)] disabled:opacity-100",
      },
      /* Tertiary — text + optional icons only (Figma: no stroke fill); Label/L; inactive status neutral 60 */
      {
        hierarchy: "tertiary",
        mode: "light",
        class:
          "h-auto min-h-0 border-0 bg-transparent p-0 text-[var(--neutral-10)] shadow-none hover:bg-transparent hover:text-[var(--brown-30)] focus-visible:ring-primary disabled:text-[var(--status-neutral-60)] disabled:hover:text-[var(--status-neutral-60)] disabled:opacity-100",
      },
      {
        hierarchy: "tertiary",
        mode: "dark",
        class:
          "h-auto min-h-0 border-0 bg-transparent p-0 text-[var(--neutral-white)] shadow-none hover:bg-transparent hover:text-[var(--brown-90)] focus-visible:ring-[var(--neutral-white)]/40 disabled:text-[var(--status-neutral-60)] disabled:hover:text-[var(--status-neutral-60)] disabled:opacity-100",
      },
      /* Tertiary icon-only — Figma Button Icon (230:1479): large 40×40, small 44×44; overrides generic tertiary h-auto */
      {
        hierarchy: "tertiary",
        mode: "light",
        size: "icon-large",
        class:
          "!h-[40px] !min-h-[40px] !w-[40px] !min-w-[40px] !max-h-[40px] !max-w-[40px] shrink-0 border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus-visible:ring-primary [&_svg:not([class*='size-'])]:size-6",
      },
      {
        hierarchy: "tertiary",
        mode: "dark",
        size: "icon-large",
        class:
          "!h-[40px] !min-h-[40px] !w-[40px] !min-w-[40px] !max-h-[40px] !max-w-[40px] shrink-0 border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus-visible:ring-[var(--neutral-white)]/40 [&_svg:not([class*='size-'])]:size-6",
      },
      {
        hierarchy: "tertiary",
        mode: "light",
        size: "icon-small",
        class:
          "!size-11 shrink-0 border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus-visible:ring-primary [&_svg:not([class*='size-'])]:size-5",
      },
      {
        hierarchy: "tertiary",
        mode: "dark",
        size: "icon-small",
        class:
          "!size-11 shrink-0 border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus-visible:ring-[var(--neutral-white)]/40 [&_svg:not([class*='size-'])]:size-5",
      },
    ],
    defaultVariants: {
      hierarchy: "primary",
      mode: "light",
      size: "large",
    },
  }
)

type LegacyVariant = NonNullable<
  VariantProps<typeof legacyButtonVariants>["variant"]
>
type LegacySize = NonNullable<
  VariantProps<typeof legacyButtonVariants>["size"]
>

type FigmaHierarchy = NonNullable<
  VariantProps<typeof figmaButtonVariants>["hierarchy"]
>
type FigmaMode = NonNullable<VariantProps<typeof figmaButtonVariants>["mode"]>
type FigmaSize = NonNullable<VariantProps<typeof figmaButtonVariants>["size"]>

export type ButtonVariantOptions = {
  className?: string | undefined
  variant?: LegacyVariant | undefined
  size?: LegacySize | FigmaSize | undefined
  /** Figma: primary / secondary / tertiary. When set, Figma matrix wins over shadcn `variant`. */
  hierarchy?: FigmaHierarchy | undefined
  /** Figma: light (default page tokens) vs dark (inverse for dark backgrounds, e.g. hero). */
  mode?: FigmaMode | undefined
  /**
   * Icon-only control (Figma Button Icon 230:1479). Uses Figma sizes `icon-large` / `icon-small`.
   * When true, `hierarchy` defaults to `primary` if omitted. An accessible name is required:
   * set `aria-label` or `aria-labelledby` (enforced by TypeScript).
   */
  iconOnly?: boolean | undefined
}

function normalizeLegacySize(size: LegacySize | FigmaSize | undefined): LegacySize {
  if (size === "large") return "lg"
  if (size === "small") return "sm"
  if (
    size === "icon-large" ||
    size === "icon-small" ||
    size === undefined
  ) {
    return "default"
  }
  return size
}

function resolveFigmaSize(
  size: LegacySize | FigmaSize | undefined,
  iconOnly: boolean | undefined
): FigmaSize {
  if (iconOnly) {
    if (
      size === "small" ||
      size === "sm" ||
      size === "icon-sm" ||
      size === "icon-small"
    ) {
      return "icon-small"
    }
    if (
      size === "icon-lg" ||
      size === "lg" ||
      size === "large" ||
      size === "icon" ||
      size === "icon-large" ||
      size === "default"
    ) {
      return "icon-large"
    }
    return "icon-large"
  }
  if (size === "small" || size === "sm") return "small"
  if (
    size === "large" ||
    size === "lg" ||
    size === "default" ||
    size === undefined
  ) {
    return "large"
  }
  if (size === "icon-large" || size === "icon-small") return size
  return "large"
}

function buttonVariants(options: ButtonVariantOptions = {}): string {
  const {
    className,
    variant = "default",
    size,
    hierarchy,
    mode = "light",
    iconOnly,
  } = options

  const resolvedHierarchy = hierarchy ?? (iconOnly ? "primary" : undefined)

  if (resolvedHierarchy) {
    return cn(
      figmaButtonVariants({
        hierarchy: resolvedHierarchy,
        mode,
        size: resolveFigmaSize(size, iconOnly),
      }),
      className
    )
  }

  return cn(
    legacyButtonVariants({
      variant,
      size: normalizeLegacySize(size),
    }),
    className
  )
}

type ButtonBaseProps = Omit<
  React.ComponentProps<"button">,
  "aria-label" | "aria-labelledby"
> &
  ButtonVariantOptions & {
    asChild?: boolean
    iconBefore?: React.ReactNode
    iconAfter?: React.ReactNode
  }

export type ButtonProps = ButtonBaseProps &
  (
    | {
        iconOnly: true
      } & (
        | { "aria-label": string; "aria-labelledby"?: undefined }
        | { "aria-labelledby": string; "aria-label"?: undefined }
      )
    | {
        iconOnly?: false | undefined
        "aria-label"?: string | undefined
        "aria-labelledby"?: string | undefined
      }
  )

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size,
      hierarchy,
      mode = "light",
      iconOnly = false,
      asChild = false,
      iconBefore,
      iconAfter,
      children,
      type = "button",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    if (
      process.env.NODE_ENV === "development" &&
      iconOnly &&
      (!ariaLabel?.trim?.() && !ariaLabelledBy?.trim?.())
    ) {
      // eslint-disable-next-line no-console -- dev-only guard when TS is bypassed
      console.error(
        "Button: iconOnly requires a non-empty aria-label or aria-labelledby for accessibility."
      )
    }

    const resolvedHierarchy = hierarchy ?? (iconOnly ? "primary" : undefined)

    const resolvedClasses = buttonVariants({
      className,
      variant,
      size,
      hierarchy,
      mode,
      iconOnly,
    })

    const showIcons = Boolean(iconBefore || iconAfter)

    const wrapIcon = (node: React.ReactNode) => (
      <span aria-hidden className="inline-flex shrink-0 items-center justify-center [&_svg]:pointer-events-none">
        {node}
      </span>
    )

    const labelledProps =
      ariaLabel !== undefined
        ? { "aria-label": ariaLabel }
        : ariaLabelledBy !== undefined
          ? { "aria-labelledby": ariaLabelledBy }
          : {}

    const innerContent = (
      <>
        {iconBefore ? wrapIcon(iconBefore) : null}
        {children}
        {iconAfter ? wrapIcon(iconAfter) : null}
      </>
    )

    if (asChild) {
      if (!showIcons) {
        return (
          <Slot
            data-slot="button"
            data-variant={resolvedHierarchy ?? variant}
            data-hierarchy={resolvedHierarchy}
            data-mode={resolvedHierarchy ? mode : undefined}
            data-size={size}
            data-icon-only={iconOnly ? "true" : undefined}
            className={resolvedClasses}
            ref={ref}
            {...labelledProps}
            {...props}
          >
            {children}
          </Slot>
        )
      }

      if (!React.isValidElement(children)) {
        return (
          <Slot className={resolvedClasses} ref={ref} {...props}>
            {children}
          </Slot>
        )
      }

      const child = children as React.ReactElement<Record<string, unknown>>
      const childProps = child.props as {
        children?: React.ReactNode
        className?: string
        ref?: React.Ref<HTMLElement>
      }

      const mergedRef = composeRefs(
        ref as React.Ref<HTMLElement>,
        childProps.ref
      )

      const mergedChildren = (
        <>
          {iconBefore ? wrapIcon(iconBefore) : null}
          {childProps.children}
          {iconAfter ? wrapIcon(iconAfter) : null}
        </>
      )

      return React.cloneElement(
        child,
        {
          ...props,
          ...labelledProps,
          "data-slot": "button",
          "data-variant": resolvedHierarchy ?? variant,
          "data-hierarchy": resolvedHierarchy,
          "data-mode": resolvedHierarchy ? mode : undefined,
          "data-size": size,
          "data-icon-only": iconOnly ? "true" : undefined,
          className: cn(
            resolvedClasses,
            childProps.className,
            "inline-flex items-center justify-center gap-2"
          ),
          children: mergedChildren,
          ref: mergedRef,
        } as Record<string, unknown>
      )
    }

    const Comp = "button"

    return (
      <Comp
        type={type}
        data-slot="button"
        data-variant={resolvedHierarchy ?? variant}
        data-hierarchy={resolvedHierarchy}
        data-mode={resolvedHierarchy ? mode : undefined}
        data-size={size}
        data-icon-only={iconOnly ? "true" : undefined}
        className={resolvedClasses}
        ref={ref}
        {...labelledProps}
        {...props}
      >
        {iconOnly && !asChild ? (
          <span
            aria-hidden
            className="inline-flex items-center justify-center [&_svg]:pointer-events-none"
          >
            {innerContent}
          </span>
        ) : (
          innerContent
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
